import usePrivateAxios from '@/common/api/usePrivateAxios'
import { defaultTransformerResponse } from '@/common/constants/defaultTransformerResponse.constant'
import { Mapper } from '@/common/services/mapper'
import { MetaResponse } from '@/common/types/metaResponse'
import { PaymentAdvanceData } from '@/features/paymentAdvances/types/paymentAdvance.type'
import { keepPreviousData, useQuery } from '@tanstack/react-query'

type UsePaymentAdvancesProps<T> = {
  filters?: {
    page?: number
    search?: string
    status?: string
    createdAtStartDate?: string
    createdAtEndDate?: string
    establishmentCompanyId?: string
  }
  mapper?: (data: any) => T
}

export const usePaymentAdvances = <T,>({
  filters: { page, search, status, createdAtStartDate, createdAtEndDate, establishmentCompanyId } = {},
  mapper = Mapper.default<T>(),
}: UsePaymentAdvancesProps<T>) => {
  const privateAxios = usePrivateAxios()

  const {
    data: { data, meta } = defaultTransformerResponse<T>(),
    isPending,
    isLoading,
    error,
    refetch,
    ...rest
  } = useQuery({
    queryKey: ['paymentAdvances', page, search, status, createdAtStartDate, createdAtEndDate, establishmentCompanyId],
    queryFn: () => {
      return privateAxios.get('/billing/payment_advances', {
        params: {
          page,
          search,
          'q[status_eq]': status,
          'q[created_at_gteq]': createdAtStartDate,
          'q[created_at_lteq]': createdAtEndDate,
          'q[establishment_company_id_eq]': establishmentCompanyId,
        },
      })
    },
    select: (data: { data: { data: PaymentAdvanceData[]; meta: MetaResponse } }) => {
      return {
        data: mapper(data?.data?.data),
        meta: data?.data?.meta,
      }
    },
    placeholderData: keepPreviousData,
  })

  return { data, meta, isPending, isLoading, error, refetch, ...rest }
}
